import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { authUserDisplayHint } from "../../../domain/auth/rallyAllyAuthEmail";
import {
  getMyProfileView,
  updateMyProfile,
  uploadMyProfilePhoto,
  removeMyProfilePhoto,
} from "../../../services/playerProfileService";
import { toUserFacingErrorMessage } from "../../../utils/userFacingError";

const PROFILE_CACHE_TTL_MS = 60_000;

/** @type {{ uid: string|null, at: number, view: object|null }} */
let profileCache = { uid: null, at: 0, view: null };

function readProfileCache(uid) {
  if (!uid || profileCache.uid !== uid || !profileCache.view) return null;
  if (Date.now() - profileCache.at > PROFILE_CACHE_TTL_MS) return null;
  return profileCache.view;
}

function writeProfileCache(uid, view) {
  if (!uid || !view) return;
  profileCache = { uid, at: Date.now(), view };
}

function clearProfileCache(uid) {
  if (uid && profileCache.uid === uid) {
    profileCache = { uid: null, at: 0, view: null };
  }
}

/**
 * @param {{ enabled?: boolean }} [options]
 */
export function useMyPlayerProfile(options = {}) {
  const { uid, profile: authProfile, user, loading: authLoading } = useAuth();
  const enabled = options.enabled !== false;
  const cached = !authLoading && uid ? readProfileCache(uid) : null;
  const [view, setView] = useState(() => cached || null);
  const [loading, setLoading] = useState(() => !cached);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const autoRetryRef = useRef(0);
  const viewRef = useRef(view);
  viewRef.current = view;

  const displayHint = authUserDisplayHint(user, authProfile);

  const reload = useCallback(
    async ({ force = false } = {}) => {
      if (!enabled) {
        setView(null);
        setLoading(false);
        return;
      }
      if (authLoading) {
        if (!viewRef.current) setLoading(true);
        return;
      }
      if (!uid) {
        setView(null);
        setLoading(false);
        setError(null);
        return;
      }
      if (force) clearProfileCache(uid);
      const stale = readProfileCache(uid) || viewRef.current;
      // Stale-while-revalidate: keep Me painted; only skeleton on cold load.
      if (!stale) setLoading(true);
      setError(null);
      try {
        const next = await getMyProfileView(uid, displayHint);
        writeProfileCache(uid, next);
        setView(next);
        autoRetryRef.current = 0;
      } catch (e) {
        setError(
          new Error(
            toUserFacingErrorMessage(e, {
              context: "profile",
              fallback: "We could not load your profile right now.",
            })
          )
        );
        if (!stale) setView(null);
      } finally {
        setLoading(false);
      }
    },
    [uid, enabled, displayHint, authLoading]
  );

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (authLoading || loading || view || error || !uid || !enabled) return;
    if (autoRetryRef.current >= 1) return;
    autoRetryRef.current += 1;
    reload();
  }, [authLoading, loading, view, error, uid, enabled, reload]);

  const saveProfile = useCallback(
    async (patch) => {
      if (!uid) return null;
      setSaving(true);
      setError(null);
      try {
        const next = await updateMyProfile(uid, patch, displayHint);
        writeProfileCache(uid, next);
        setView(next);
        return next;
      } catch (e) {
        const err = new Error(
          toUserFacingErrorMessage(e, {
            context: "profile",
            fallback: "Could not save your profile.",
          })
        );
        setError(err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [uid, displayHint]
  );

  const uploadPhoto = useCallback(
    async (file, meta = {}) => {
      if (!uid) return null;
      setSaving(true);
      setError(null);
      try {
        const next = await uploadMyProfilePhoto(uid, file, {
          onProgress: meta.onProgress,
        });
        writeProfileCache(uid, next);
        setView(next);
        return next;
      } catch (e) {
        const err = new Error(
          toUserFacingErrorMessage(e, {
            context: "profile",
            fallback: "Could not update your photo.",
          })
        );
        setError(err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [uid]
  );

  const removePhoto = useCallback(
    async (meta = {}) => {
      if (!uid) return null;
      setSaving(true);
      setError(null);
      try {
        const next = await removeMyProfilePhoto(uid, {
          onProgress: meta.onProgress,
        });
        writeProfileCache(uid, next);
        setView(next);
        return next;
      } catch (e) {
        const err = new Error(
          toUserFacingErrorMessage(e, {
            context: "profile",
            fallback: "Could not remove your photo.",
          })
        );
        setError(err);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [uid]
  );

  const forceReload = useCallback(() => reload({ force: true }), [reload]);

  return {
    view,
    loading: (loading || authLoading) && !view,
    saving,
    error,
    reload: forceReload,
    saveProfile,
    uploadPhoto,
    removePhoto,
    authLoading,
    uid,
  };
}
